import { useEffect, useSyncExternalStore } from "react";
import { useBlocker } from "react-router-dom";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  getNavigationGuardMessage,
  hasNavigationGuard,
  notifyNavigationBlocked,
  subscribeNavigationGuards,
} from "@/lib/navigationGuardRegistry";

/**
 * The single navigation blocker for the whole application.
 *
 * It sits in the root layout of the data router, so it covers every way a
 * route can change: header and sidebar links, the browser Back and Forward
 * buttons, and `navigate()` calls made in code. Closing or refreshing the tab
 * is a separate browser-controlled prompt (`beforeunload`), which is a last
 * resort and cannot be styled or relied upon.
 */
export default function NavigationGuardPrompt() {
  const message = useSyncExternalStore(
    subscribeNavigationGuards,
    getNavigationGuardMessage,
    () => null
  );

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    // Read the registry live rather than the last rendered value. A required
    // exit — signing out, a revoked session — changes the registry and
    // navigates in the same tick, before React has re-rendered, so the
    // rendered value would still say "blocked" and trap the person.
    if (!hasNavigationGuard()) return false;
    // A change of search or hash on the same screen is still leaving the work
    // behind when the page keys off it, so only an identical URL is exempt.
    return (
      currentLocation.pathname !== nextLocation.pathname ||
      currentLocation.search !== nextLocation.search
    );
  });

  // If the work finishes (or is discarded) while the question is on screen,
  // there is nothing left to protect — let the person through.
  useEffect(() => {
    if (blocker.state === "blocked" && !message) blocker.proceed?.();
  }, [blocker, message]);

  useEffect(() => {
    if (!message) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [message]);

  const open = blocker.state === "blocked" && Boolean(message);

  return (
    <AlertDialog open={open}>
      <AlertDialogContent
        className="max-w-md"
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>Leave this page?</AlertDialogTitle>
          <AlertDialogDescription>{message}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="gap-2 sm:gap-2">
          <AlertDialogCancel
            className="min-h-[44px]"
            onClick={() => {
              notifyNavigationBlocked();
              blocker.reset?.();
            }}
          >
            Stay on this page
          </AlertDialogCancel>
          <AlertDialogAction
            className="min-h-[44px]"
            onClick={() => blocker.proceed?.()}
          >
            Leave anyway
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
